import { Link, useParams } from 'react-router-dom';
import { formatDateTime } from '../../lib/api';
import type { PublicNotice } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const KNOWN_KEYS = new Set(['title', 'summary']);

export function NoticeDetail() {
  const { id = '' } = useParams();
  const q = useApiQuery<PublicNotice>(`/api/v1/opengov/notices/${encodeURIComponent(id)}`);
  const payload = (q.data?.payload ?? {}) as Record<string, unknown>;
  const extraKeys = Object.keys(payload).filter((k) => !KNOWN_KEYS.has(k));

  return (
    <div>
      <Link to="/notices" className="mb-4 inline-block text-sm font-semibold text-brand-600 hover:underline">
        ‹ Back to Public Notices
      </Link>

      {q.loading ? <Spinner /> : null}
      {q.error ? <ErrorNote message={q.error} /> : null}

      {q.data ? (
        <>
          <PageHeader
            title={typeof payload.title === 'string' ? payload.title : `${q.data.type} notice`}
            subtitle={`Published ${formatDateTime(q.data.publishedAt)} · source: ${q.data.sourceModule}`}
            actions={<StatusBadge status={q.data.type} />}
          />

          {typeof payload.summary === 'string' ? <p className="mb-6 max-w-3xl text-sm text-slate-700">{payload.summary}</p> : null}

          {extraKeys.length > 0 ? (
            <div className="mb-6">
              <h2 className="mb-2 text-sm font-bold text-slate-700">Details</h2>
              <div className="card overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    {extraKeys.map((k) => (
                      <tr key={k} className="hover:bg-slate-50">
                        <td className="table-cell w-48 font-semibold capitalize text-slate-600">{k.replaceAll(/([A-Z])/g, ' $1').trim()}</td>
                        <td className="table-cell">{String(payload[k])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <details className="text-xs text-slate-400">
            <summary className="cursor-pointer select-none font-semibold">Raw notice payload</summary>
            <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-50 p-3 text-slate-600">{JSON.stringify(q.data.payload, null, 2)}</pre>
          </details>
        </>
      ) : null}
    </div>
  );
}
